/**
 * Framework for all React components injected into trackingAPI at root level
 *
 * @author Ryan Pape <rpape@wayfair.com>
 * @copyright 2018 Wayfair LLC - All rights reserved
 */
/* eslint-env browser */

import _ from 'underscore';
import Scribe from './scribe';
// @TODO scribe internal (admin/extranet)
// import ScribeInternal from './scribe_internal';
// import SCRIBE_INTERNAL_EVENTS from './scribe_internal_event_constants';
import SCRIBE_EVENTS from './scribe_event_constants';
// @TODO sribe in view
// import {INACTIVE_IN_VIEW, PAGE_ENDED} from './in_view/scribe_in_view_triggers';
import btoa from 'btoa';
import initLegacyPageTracking from './scribe_init_page_tracking';
import visibility from './visibility';

const COOKIE_API_DOESNT_EXIST_IN_CONFIG_ERROR =
  'Cookie Api does not exist in Scribe config. Please refer to Scribe documentation: ';

const INACTIVE_IN_VIEW = 'InactiveInView'; // User has not seen a new in-view object in some time
const PAGE_ENDED = 'PageEnded'; // User closes tab/window or loads a new page

const defaultConfig = {
  // data from server
  transactionID: null,
  storeID: null,
  storeDomain: null,
  adminDomain: null,
  useInternalEvents: false,
  // framework utils
  logger: null,
  cookieApi: null,
};

function createScribe(userConfig = defaultConfig) {
  if (!userConfig.cookieApi) {
    throw new Error(COOKIE_API_DOESNT_EXIST_IN_CONFIG_ERROR);
  }
  const config = {...defaultConfig, ...userConfig};
  const scribeInstance = new Scribe(config);

  const sendBatchedEvents = scribeInstance.client.sendBatchedEvents.bind(
    scribeInstance.client
  );

  const sendEvent = scribeInstance.client.sendEvent.bind(scribeInstance.client);
  const recordEvent = scribeInstance.recordEvent.bind(scribeInstance);
  const action = SCRIBE_EVENTS.WEB_ACTION;
  const click = SCRIBE_EVENTS.WEB_CLICK;
  const sandbox = SCRIBE_EVENTS.SANDBOX;
  const impression = SCRIBE_EVENTS.SITE_IMPRESSION;

  // if (config.useInternalEvents) {
  //   const scribeInternalInstance = new ScribeInternal(config);
  //   sendEvent = scribeInternalInstance.client.sendEvent.bind(
  //     scribeInternalInstance.client
  //   );

  //   action = SCRIBE_INTERNAL_EVENTS.INTERNAL_WEB_ACTION;
  //   click = SCRIBE_INTERNAL_EVENTS.INTERNAL_WEB_CLICK;
  //   sandbox = SCRIBE_INTERNAL_EVENTS.INTERNAL_SANDBOX;
  //   impression = SCRIBE_INTERNAL_EVENTS.INTERNAL_SITE_IMPRESSION;
  // }

  const eventTypes = {action, click, sandbox, impression};
  // In-View
  const INACTIVE_IN_VIEW_TIMEOUT = 60000; // Time page must be inactive until in-view items are tracked
  let lastInView; // Data to track with last in-view items when page ends
  let inViewIndex = 0; // Index of in-view event tracked on page
  let inView = {}; // Items viewed by user, sorted by type {type: {field: value}}
  // Send in-view items to Scribe
  const trackInView = ({trigger, ...meta}) => {
    // Only send Scribe event if items were viewed
    if (!Object.keys(inView).length) {
      return;
    }
    const events = Object.keys(inView).map(type => {
      // Only track list fields if they are used at least once (they will have all empty values otherwise)
      const lists = {};
      const {objects, ...rest} = inView[type];
      // Slush field objects contains JSON blobs
      if (objects && objects.some(object => Object.keys(object).length)) {
        lists.objects = JSON.stringify(objects);
      }
      // All other list fields contain non-iterable values
      Object.entries(rest).forEach(([field, list]) => {
        if (list.some(values => values)) {
          lists[field] = list;
        }
      });
      return {
        trigger,
        ...meta,
        eventType: SCRIBE_EVENTS.IN_VIEW,
        index: inViewIndex,
        transactionId: config.transactionID,
        pageType: config.pageType,
        type,
        ...lists,
      };
    });
    sendBatchedEvents({events});
    inView = {};
    inViewIndex++;
  };
  const activeInView = _.debounce(trackInView, INACTIVE_IN_VIEW_TIMEOUT);
  // Queue in-view item to track later
  const pendingInView = ({type, fields, ...pending}) => {
    lastInView = pending;
    if (!inView.hasOwnProperty(type)) {
      inView[type] = {};
    }
    const viewed = inView[type];
    Object.keys(fields).forEach(field => {
      if (!viewed.hasOwnProperty(field)) {
        viewed[field] = [];
      }
      viewed[field].push(fields[field]);
    });
    activeInView({trigger: INACTIVE_IN_VIEW, ...lastInView});
  };

  // Init in-view
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () =>
      trackInView({
        trigger: PAGE_ENDED,
        ...lastInView,
      })
    );
  }

  const baseDataGetters = {
    getServiceId: () =>
      typeof config.serviceId !== 'undefined'
        ? {serviceId: config.serviceId}
        : null,
    getPageType: () =>
      typeof config.pageType !== 'undefined'
        ? {pageType: config.pageType}
        : null,
  };

  const setClickLocation = (
    clickLocation,
    clickLocationMetadata,
    destinationUrl
  ) => {
    let hash = 'empty';
    if (destinationUrl && destinationUrl.length) {
      hash = btoa(destinationUrl)
        .toString()
        .slice(-5); // grab last five characters of the hash
    }
    // 1. when no destinationUrl passed, clickLocationMetadata will say that it is empty
    // 2. when destinationUrl is passed, it will hold 5 characters of a hash
    // 3. all of the above will be appended to the beginning of any metadata passed
    // 4. the string concatenation will make all of it a string, which will ensure that any desired 'null' or '0' metadata
    //    is carried through
    const metadata = hash + clickLocationMetadata;
    config.cookieApi.setCookie('ClickLocation', clickLocation);
    config.cookieApi.setCookie('ClickLocationMetadata', metadata);
  };

  const initPageTracking = () =>
    initLegacyPageTracking({
      client: scribeInstance,
      applicationServerKey: userConfig.applicationServerKey,
      setClickLocation,
    });

  return {
    trackInView,
    pendingInView,
    sendEvent,
    eventTypes,
    baseDataGetters,
    setClickLocation,
    visibility,
    config,
    initPageTracking,
    scribeInstance,
    recordEvent,
  };
}

export {createScribe, SCRIBE_EVENTS};
